// Pieces more than one page shows: the users table, the orders table, the
// node cards, what a computer is running.

import { api, query, send } from "./api";
import type { Acct, AcctPage, Block, NodeCard, Nodes, Order, OrdersPage, Running } from "./types";
import { ago, color, confirmButton, date, dateTime, days, flash, geoCell, h, hours, link, loadChip, meters, money, num, rate, size, sparkline, statusChip, table, td, th, uptime, userLink, who } from "./ui";

const PLAN_NAMES: Record<string, string> = { free: "Free", basic: "Basic", pro: "Pro", team: "Team", enterprise: "Enterprise" };
export const planName = (id: string) => PLAN_NAMES[id] ?? id;

export const adminOrders = (params: Record<string, string | number> = {}) => api<OrdersPage>(`/v1/admin/orders?${query(params)}`);
export const acctPage = (params: Record<string, string | number>) => api<AcctPage>(`/v1/admin/accounts?${query({ days: days(), ...params })}`);

export function usersTable(list: Acct[], limit?: number) {
  const rows = (limit ? list.slice(0, limit) : list).map((a) =>
    h(
      "tr",
      null,
      h("td", null, userLink(a.id, who(a)), h("span", { class: "muted block" }, a.email ?? a.id)),
      td(`${a.online}/${a.computers}`, "num"),
      td(String(a.clients), "num"),
      td(size(a.relay_out), "num"),
      td(size(a.relay_in), "num"),
      td(String(a.sessions), "num"),
      td(hours(a.session_seconds), "num"),
      td(String(a.computer_days), "num"),
      td(String(a.active_shares), "num"),
      geoCell(a),
      td(ago(a.last_seen), "num"),
      td(date(a.first_seen), "num"),
    ),
  );
  return table(
    [th("User"), th("Computers", "num"), th("Phones", "num"), th("To", "num"), th("From", "num"), th("Sessions", "num"), th("Session time", "num"), th("PC-days", "num"), th("Shares", "num"), th("Location"), th("Last active", "num"), th("Joined", "num")],
    rows,
    "No accounts yet.",
  );
}

export const orderLine = (o: { plan: string; period: string; seats: number }) => `${planName(o.plan)}, 1 ${o.period}${o.seats > 1 ? `, ${o.seats} seats` : ""}`;

export function ordersTable(orders: Order[], limit?: number) {
  const rows = (limit ? orders.slice(0, limit) : orders).map((o) =>
    h(
      "tr",
      null,
      h("td", null, link(`/order?id=${encodeURIComponent(o.id)}`, o.id)),
      h("td", null, userLink(o.account_id, o.username || o.email || o.account_id.slice(0, 8))),
      td(orderLine(o)),
      td(dateTime(o.created_at), "num"),
      td(o.ends_at ? `${date(o.starts_at ?? o.created_at)} – ${date(o.ends_at)}` : "—", "num"),
      h("td", null, statusChip(o.status, o.status === "manual" ? "granted by staff, no payment" : undefined), o.refunded_amount ? h("span", { class: "muted" }, ` ${money(o.refunded_amount / 100)} refunded`) : ""),
      td(o.status === "manual" ? "granted" : money(o.amount / 100), "num"),
    ),
  );
  return table([th("Order"), th("User"), th("Plan"), th("Created", "num"), th("Term", "num"), th("Status"), th("Total", "num")], rows, "No orders.");
}

/** up / down / no reports, from the last minute report. */
export const stateChip = (st: string, reported: number | null) =>
  h("span", { class: `chip ${st === "up" ? "online" : st === "down" ? "failed" : "ended"}`, title: reported ? `last report ${ago(reported)}` : "never reported" }, st === "up" ? "up" : st === "down" ? "down" : "no reports");

/** pending / dark / retired / unregistered; nothing for an active node. */
export const nodeStateChip = (n: { node_state: string; enrol_pending?: boolean }) => {
  if (n.node_state === "active") return null;
  const title: Record<string, string> = {
    pending: n.enrol_pending ? "added, waiting for the install command to run" : "added; its enrol token has expired, issue a new one",
    dark: "out of rotation: no new computers are sent here",
    retired: "retired: out of rotation, rules and pins",
    unregistered: "reports with the shared key but has no record; add it as a node",
  };
  return h("span", { class: `chip ${n.node_state}`, title: title[n.node_state] ?? "" }, n.node_state);
};

export function nodeCards(nodes: Nodes) {
  return h(
    "div",
    { class: "nodes" },
    ...nodes.nodes.map((x: NodeCard) =>
      h(
        "a",
        { class: "node", href: `/node?relay=${encodeURIComponent(x.relay)}` },
        h("div", { class: "node-head" }, h("i", { class: "dot", style: `background:${color(x.relay)}` }), h("b", null, x.relay), h("span", { class: "muted" }, x.label), stateChip(x.state, x.reported_at), nodeStateChip(x), loadChip(x.capacity)),
        h(
          "div",
          { class: "node-stats" },
          h("span", null, h("b", null, num(x.reservations)), " computers"),
          h("span", null, h("b", null, num(x.circuits)), " circuits"),
          h("span", null, h("b", null, num(x.peers)), " devices"),
        ),
        h("div", { class: "node-rate" }, `↓ ${rate(x.rate_out)} · ↑ ${rate(x.rate_in)} now`),
        meters(x.capacity, true),
        sparkline(x.trend.map((t) => t.relay_in + t.relay_out), color(x.relay), `${x.relay} traffic, last 24 hours`),
        h("div", { class: "muted small" }, `24 h · ${size(x.relay_in + x.relay_out)} in ${days()} days · ${x.accounts} accounts · ${x.version ?? "?"} · up ${uptime(x.uptime_s)} · key ${x.key}`),
      ),
    ),
  );
}

export const blockWhat = (b: Block) =>
  b.kind === "ip"
    ? b.value
    : b.kind === "account"
      ? `${b.who ?? b.value.slice(0, 12)}`
      : b.kind === "device"
        ? `${b.detail.name || b.value}${b.who ? ` (${b.who})` : ""}`
        : `${b.detail.kind === "kill_session" ? "session" : b.detail.kind === "stop_run" ? "agent run" : "share"} on ${b.detail.computer ?? "a computer"}`;

/** Ends a session, run or share on a computer, as staff. */
async function endOnComputer(deviceId: string, target: { session_id?: string; run_id?: string; share_id?: string }) {
  const r = await send<{ effect: string }>("POST", "/v1/admin/blocks", { kind: "session", device_id: deviceId, ...target });
  flash(r.effect);
}

/** What a computer is running, each with an End button (when the viewer may). */
export async function runningPanel(deviceId: string, write: boolean): Promise<HTMLElement> {
  const r = await send<Running>("GET", `/v1/admin/devices/${encodeURIComponent(deviceId)}/running`);
  const act = (label: string, armed: string, target: { session_id?: string; run_id?: string; share_id?: string }) => (write ? h("td", { class: "num" }, confirmButton(label, armed, () => endOnComputer(deviceId, target))) : td("", "num"));
  const rows: HTMLElement[] = [
    ...r.sessions.map((s) => h("tr", null, td("Terminal session"), td(`${s.name} (${s.windows} ${s.windows === 1 ? "window" : "windows"})`), td(s.created ? ago(s.created) : "—", "num"), act("End", `End ${s.name}`, { session_id: s.id }))),
    ...r.runs.map((x) => h("tr", null, td("Agent run"), td(`${x.agent} · ${x.state}`), td(x.started ? ago(x.started) : "—", "num"), act("Stop", "Stop it", { run_id: x.id }))),
    ...r.shares.map((x) => h("tr", null, td("Shared window"), td(`${x.window_label || x.id} · ${x.guests} joined`), td(`until ${dateTime(x.expires_at)}`, "num"), act("End", "End the share", { share_id: x.id }))),
  ];
  return h(
    "div",
    null,
    h("p", { class: "muted" }, r.reported_at ? `Reported ${ago(r.reported_at)}${r.version ? ` by Luwu ${r.version}` : ""}. Sessions show from Luwu 0.2.15 on.` : "This computer hasn't reported yet."),
    table([th("What"), th("Name"), th("Since", "num"), th("", "num")], rows, "Nothing running."),
  );
}

